import { useState, useRef, useEffect } from "react";
import "../styles/dfs.css";
import stackdfs from '../../../assets/Images/StackDFS.png'
import visiteDFS from '../../../assets/Images/visiteDFS.png'
import dfstrav from '../../../assets/Images/dfstrav.jpg'
import dfsapp from '../../../assets/Images/dfsapp.png'
import Graph from "./Graph";
import { visualizeDFS } from "../algorithms/DFSalgo";
import Button from "../../shared/Button";

export default function DepthFs() {
  const [inputValue, setInputValue] = useState("");
  const [graph, setGraph] = useState({});
  const [dfsOrder, setDfsOrder] = useState([]);
  const [traversedNode, setTraversedNode] = useState("");
  const [backtrackingNode, setBacktrackingNode] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [stackNodes, setStackNodes] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const controllerRef = useRef(null);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleAddNode = () => {
    const newGraph = { ...graph };
    const inputParts = inputValue.split(":").map((part) => part.trim());

    if (inputParts.length !== 2) {
      alert('Input must be in the format "Parent: Child1, Child2"');
      return;
    }

    const parentNode = inputParts[0];
    const children = inputParts[1].split(",").map((child) => child.trim());

    if (!newGraph[parentNode]) newGraph[parentNode] = [];
    newGraph[parentNode] = [...newGraph[parentNode], ...children];

    children.forEach((child) => {
      if (!newGraph[child]) newGraph[child] = [];
    });

    setGraph(newGraph);
    setInputValue("");
  };

  const handleDFS = async () => {
    if (isRunning) return;

    setDfsOrder([]);
    setVisitedNodes([]);
    setStackNodes([]);
    setTraversedNode("");
    setBacktrackingNode("");
    setIsRunning(true);
    setIsPaused(false);

    controllerRef.current = {
      isPaused: false,
      pause: () => {
        controllerRef.current.isPaused = true;
        setIsPaused(true);
      },
      resume: () => {
        controllerRef.current.isPaused = false;
        setIsPaused(false);
        controllerRef.current.resolvePromise?.();
      },
    };

    try {
      const order = await visualizeDFS(
        graph,
        setTraversedNode,
        setVisitedNodes,
        setStackNodes,
        setBacktrackingNode,
        controllerRef.current
      );
      setDfsOrder(order);
    } finally {
      setIsRunning(false);
      setIsPaused(false);
    }
  };

  const handlePause  = () => controllerRef.current?.pause();
  const handleResume = () => controllerRef.current?.resume();

  const handleClear = () => {
    setGraph({});
    setDfsOrder([]);
    setVisitedNodes([]);
    setStackNodes([]);
    setTraversedNode("");
    setBacktrackingNode("");
    setIsRunning(false);
    setIsPaused(false);
    setInputValue("");
  };

  return (
    <div className="Dapp-container">
      <div className="Dsidebar">
        <h1>Graph DFS Visualizer</h1>

        <div className="Dinput-container">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Enter nodes (e.g., A: B, C)"
          />
          <Button onClick={handleAddNode} disabled={isRunning}>Add Node</Button>
          <Button onClick={handleDFS} disabled={isRunning}>Run DFS</Button>
          <Button variant="danger" onClick={handleClear}>Clear</Button>
          <Button variant="" onClick={() => setShowPopup(true)}>DFS Conditions</Button>
        </div>

        {isRunning && (
          <div className="Dcontrols-container">
            {isPaused ? (
              <Button onClick={handleResume}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                Resume
              </Button>
            ) : (
              <Button variant="secondary" onClick={handlePause}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
                Pause
              </Button>
            )}
          </div>
        )}

        <div className="Doutput-container">
          <h2>DFS Order:</h2>
          <div className="dfs-order-display">
            {dfsOrder.map((node, index) => (
              <span key={index} className="dfs-order-item">{node}</span>
            ))}
          </div>
        </div>

        <div className="Darray-container">
          <h3>Visited Nodes:</h3>
          <div className="Darray-display">
            {visitedNodes.map((node, index) => (
              <span key={index}>{node}</span>
            ))}
          </div>
        </div>

        <div className="Darray-container">
          <h3>Stack:</h3>
          <div className="Darray-display">
            {stackNodes.map((node, index) => (
              <span key={index}>{node}</span>
            ))}
          </div>
        </div>

        <div className="legends">
          <div className="legend-item">
            <div className="legend-color traversing"></div>
            <span>Traversing</span>
          </div>
          <div className="legend-item">
            <div className="legend-color backtracking"></div>
            <span>Backtracking</span>
          </div>
          <div className="legend-item">
            <div className="legend-color visited-node"></div>
            <span>Visited</span>
          </div>
          <div className="legend-item">
            <div className="legend-color default-node"></div>
            <span>Default</span>
          </div>
        </div>
      </div>

      <div className="Dmain-content">
        <div className="Dvisualization">
          <Graph
            nodes={graph}
            traversedNode={traversedNode}
            backtrackingNode={backtrackingNode}
            visitedNodes={visitedNodes}
          />
        </div>
      </div>

      {showPopup && <DFSConditionsPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
}

function DFSConditionsPopup({ onClose }) {
  return (
    <div className="Dpopup-overlay">
      <div className="Dpopup-content">
        <div className="Dpopup-close-row">
          <h2>DFS Conditions</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
        </div>
        <div className="conditions">
          <h3>1. Stack Data Structure</h3>
          <p>DFS uses a stack (or recursion) to keep track of nodes to visit next.</p>
          <img src={stackdfs} alt="Stack illustration" />

          <h3>2. Visited Set</h3>
          <p>A set is used to keep track of visited nodes to avoid cycles.</p>
          <img src={visiteDFS} alt="Visited set illustration" />

          <h3>3. Depth-First Traversal</h3>
          <p>DFS explores as far as possible along each branch before backtracking.</p>
          <img src={dfstrav} alt="Depth-first traversal illustration" />

          <h3>4. Applications</h3>
          <p>DFS is useful for topological sorting, finding connected components, and solving puzzles like mazes.</p>
          <img src={dfsapp} alt="DFS applications illustration" />
        </div>
      </div>
    </div>
  );
}
